
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, Injector, Signal, ViewEncapsulation, computed, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { BaseMixinComponent, RefreshableComponentMixin } from '@mixins/components';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { HighlightMeSceneDirective } from '@shared/highlight-me';
import { stopAndPreventEvent } from '@utils/dom/event';
import { MirIcon } from '@widgets/mir-icon';
import { WizardBasket } from '@wizard/models';
import { combineLatest } from 'rxjs';
import { WizardPanelControlsComponent, WizardPanelListComponent } from './components';
import { WizardItemEntity } from './models';
import { WizardPanelController } from './services';

export type WizardPanelMode = 'editing' | 'viewing';

@UntilDestroy()
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  selector: 'mir-wizard-panel',
  templateUrl: './wizard-panel.component.html',
  imports: [
    CommonModule,
    TranslateModule,
    MirIcon,
    HighlightMeSceneDirective,
    WizardPanelControlsComponent,
    WizardPanelListComponent,
  ]
})
export class WizardPanelComponent extends RefreshableComponentMixin(BaseMixinComponent) {

  @HostBinding('class')
  get hostCssClass(): string {
    return `mir-wizard-panel
            z-10 overflow-hidden
            block size-full max-h-full`;
  }

  /** @Input */
  readonly mode = input<WizardPanelMode>('viewing');

  /** @Inject */
  readonly #wizardPanelController = inject(WizardPanelController);

  protected readonly hasNoItem: Signal<boolean>;
  protected readonly isEditable: Signal<boolean>;
  protected readonly isLocked: Signal<boolean>;
  protected readonly hideFooter: Signal<boolean>;
  protected readonly basket = signal<WizardBasket>(null);
  protected readonly hasNoBasket = signal(true);
  protected readonly isResultDisplayed = signal(false);
  protected readonly isOptionsPanelDisplayed = signal(false);
  protected readonly isEditing = computed(() => this.isEditable() && this.mode() === 'editing');
  protected readonly updated = signal(false);

  constructor() {
    const injector = inject(Injector);

    super();

    this.isEditable = toSignal(
      this.#wizardPanelController.isEditable$,
      { initialValue: false, injector }
    );

    this.isLocked = toSignal(
      this.#wizardPanelController.isLocked$,
      { initialValue: true, injector }
    );

    this.hideFooter = toSignal(
      this.#wizardPanelController.hideFooter$,
      { initialValue: true, injector }
    );

    this.hasNoItem = toSignal(
      this.#wizardPanelController.isBasketEmpty$,
      { initialValue: true, injector }
    );

    combineLatest([
      this.#wizardPanelController.updatedFromSettings$,
      this.#wizardPanelController.updatedFromBroadcastChannel$,
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(flags => {

        this.updated.set(flags[0] || flags[1]);
        this.refresh();
      });

    effect(() => {
      const basket = this.#wizardPanelController.basket();
      const hasBasket = Boolean(basket);

      this.basket.set(basket);
      this.hasNoBasket.set(!hasBasket);

      if (!this.isOptionsPanelDisplayed() && hasBasket) {
        this.isOptionsPanelDisplayed.set(true);
      }
    });

    effect(() => {
      const isResultDisplayed = this.#wizardPanelController.isResultDisplayed();
      this.isResultDisplayed.set(isResultDisplayed);
    });
  }

  get dataList(): WizardItemEntity[] { return this.#wizardPanelController.dataList; }

  handleToggleExpand() {
    this.#wizardPanelController.toggleWizardPanel();
  }

  handleNewBasket(event: MouseEvent) {
    stopAndPreventEvent(event);
    this.#wizardPanelController.newBasket().then(() => {
      this.isOptionsPanelDisplayed.set(true);
    });
  }

  handleDisplayResults(event: Event) {
    stopAndPreventEvent(event);
    this.#wizardPanelController.displayResults();
  }

  handleRefreshResults(event: Event) {
    stopAndPreventEvent(event);
    this.#wizardPanelController.refreshResults();
    this.updated.set(false);
  }

  handleOptionsPanelStateChange(open: boolean) {
    this.isOptionsPanelDisplayed.set(open);
  }
}
